<script setup>
// Auto-extracted chrome for PvHeizung.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "PV-Heizung" },
    brandTagline: { type: String, default: "PV + Wärmepumpe für Ihr Haus - in einer Auslegung." },
    brandAccent: { type: String, default: '#0EA5E9' },
    brandCta: { type: String, default: '#000' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "PV-Heizung");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "PV + Wärmepumpe für Ihr Haus - in einer Auslegung.");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');
const brandCta = computed(() => props.brandCta);

const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=JetBrains+Mono:wght@500;600&family=Inter:wght@400;500;600;700&display=swap";
</script>

<template>
    <div class="pvh" :style="`--pvh-accent:${brandAccent};--pvh-cta:${brandCta};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="pvh-shell">
                    <div class="pvh-shell__l">
                        <span class="pvh-shell__dot"></span>
                        <strong>{{ brandName }}</strong>
                        <span class="pvh-shell__div">/</span>
                        <span class="pvh-shell__tag">{{ brandTagline }}</span>
                    </div>
                    <a href="#pvh-config" class="pvh-shell__cta">Anfrage starten &rarr;</a>
                </header>
        <slot />
        <footer class="pvh-foot">
                    <div class="pvh-foot__inner">
                        <p>{{ brandName }} &middot; {{ brandTagline }}</p>
                        <nav><a href="/impressum">Impressum</a><a href="/datenschutz">Datenschutz</a><a href="/agb">AGB</a><a href="/widerruf">Widerruf</a><a href="/ratgeber">Ratgeber</a></nav>
                        <p class="pvh-foot__copy">&copy; {{ new Date().getFullYear() }} pvheizung.de &middot; Auslegungen ohne Gewähr.</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.pvh { --pvh-bg: #0b1220; --pvh-paper: #111827; --pvh-card: #1f2937; --pvh-line: #1f2937; --pvh-line-soft: #374151; --pvh-ink: #f3f4f6; --pvh-sub: #94a3b8; --pvh-muted: #6b7280;
    font-family: 'Inter', system-ui, sans-serif; color: var(--pvh-ink); background: var(--pvh-bg); min-height: 100dvh; line-height: 1.55; -webkit-font-smoothing: antialiased; }
.pvh *, .pvh *::before, .pvh *::after { box-sizing: border-box; }
.pvh h1, .pvh h2, .pvh h3, .pvh h4 { font-family: 'Space Grotesk', system-ui, sans-serif; font-weight: 700; letter-spacing: -0.02em; line-height: 1.1; margin: 0; color: var(--pvh-ink); }
.pvh h1 em, .pvh h2 em { font-style: normal; color: var(--pvh-cta); }
.pvh-mono { font-family: 'JetBrains Mono', 'SF Mono', monospace; font-variant-numeric: tabular-nums; }

/* Top shell */
.pvh-shell { max-width: 1320px; margin: 0 auto; padding: 18px 24px; display: flex; align-items: center; justify-content: space-between; gap: 16px; border-bottom: 1px solid var(--pvh-line); }
.pvh-shell__l { display: inline-flex; align-items: center; gap: 12px; font-size: 14px; color: var(--pvh-ink); }
.pvh-shell__dot { width: 8px; height: 8px; background: var(--pvh-cta); border-radius: 50%; box-shadow: 0 0 0 4px rgba(234, 88, 12, 0.15); }
.pvh-shell__div { color: var(--pvh-muted); }
.pvh-shell__tag { color: var(--pvh-sub); font-size: 13px; }
.pvh-shell__cta { color: var(--pvh-cta); text-decoration: none; font-weight: 600; font-size: 13px; padding: 8px 14px; border: 1px solid var(--pvh-line-soft); border-radius: 4px; transition: all 200ms ease; }
.pvh-shell__cta:hover { border-color: var(--pvh-cta); }

/* Hero intro */
.pvh-intro { max-width: 1320px; margin: 0 auto; padding: 64px 24px 48px; display: grid; grid-template-columns: 1fr; gap: 40px; align-items: end; }
@media (min-width: 980px) { .pvh-intro { grid-template-columns: 1.4fr 1fr; gap: 64px; padding: 96px 24px 64px; } }
.pvh-intro__chip { font-size: 12px; color: var(--pvh-sub); margin-bottom: 16px; letter-spacing: 0.06em; }
.pvh-intro__chip .pvh-mono { padding: 2px 8px; background: var(--pvh-card); border-radius: 3px; color: var(--pvh-cta); font-weight: 600; font-size: 11px; }
.pvh-intro__h { font-size: clamp(40px, 6.5vw, 72px); margin-bottom: 18px; text-wrap: balance; }
.pvh-intro__sub { color: var(--pvh-sub); font-size: 17px; max-width: 56ch; margin-bottom: 28px; }
.pvh-intro__row { display: flex; flex-wrap: wrap; align-items: center; gap: 18px; }
.pvh-intro__meta { font-size: 13px; color: var(--pvh-muted); }

/* Buttons */
.pvh-btn { display: inline-flex; align-items: center; justify-content: center; gap: 10px; font-family: inherit; font-weight: 600; font-size: 14px; padding: 12px 22px; border-radius: 4px; border: 1px solid transparent; cursor: pointer; transition: all 220ms cubic-bezier(0.32,0.72,0,1); text-decoration: none; }
.pvh-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pvh-btn--cta { background: var(--pvh-cta); color: #fff; }
.pvh-btn--cta:hover:not(:disabled) { background: #c2410c; transform: translateY(-1px); }
.pvh-btn--ghost { background: transparent; color: var(--pvh-sub); border-color: var(--pvh-line-soft); }
.pvh-btn--ghost:hover { color: var(--pvh-ink); border-color: var(--pvh-ink); }
.pvh-btn--lg { padding: 16px 28px; font-size: 15px; }
.pvh-btn--sm { padding: 8px 14px; font-size: 12px; }
.pvh-btn--full { width: 100%; }
.pvh-arrow { transition: transform 200ms ease; }
.pvh-btn:hover .pvh-arrow { transform: translateX(2px); }

/* Spec card */
.pvh-spec { display: flex; flex-direction: column; gap: 24px; justify-content: center; align-items: stretch; }
.pvh-hero-photo { margin: 24px 0 0; }
.pvh-hero-photo img { width: 100%; height: auto; display: block; border: 1px solid var(--pvh-line-soft, #e5e7eb); }
.pvh-hero-photo figcaption { font-size: 12px; color: var(--pvh-sub, #64748b); margin-top: 8px; }
.pvh-spec-photo { margin: 0; }
.pvh-spec-photo img { width: 100%; height: auto; display: block; border: 1px solid var(--pvh-line-soft, #e5e7eb); }
.pvh-spec-photo figcaption { font-size: 12px; color: var(--pvh-sub, #64748b); margin-top: 8px; }
.pvh-spec__card { background: var(--pvh-paper); border: 1px solid var(--pvh-line-soft); border-radius: 6px; padding: 28px; width: 100%; }
.pvh-spec__chip { font-size: 11px; color: var(--pvh-cta); text-transform: uppercase; letter-spacing: 0.14em; font-weight: 700; margin-bottom: 20px; }
.pvh-spec__grid { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 18px 24px; }
.pvh-spec__grid li { display: flex; flex-direction: column; gap: 4px; padding-bottom: 12px; border-bottom: 1px dotted var(--pvh-line-soft); }
.pvh-spec__grid span { font-size: 11px; color: var(--pvh-sub); text-transform: uppercase; letter-spacing: 0.1em; font-weight: 600; }
.pvh-spec__grid strong { font-size: 22px; color: var(--pvh-ink); font-weight: 600; }

/* Config */
.pvh-config { padding: 80px 24px; background: var(--pvh-paper); border-top: 1px solid var(--pvh-line); border-bottom: 1px solid var(--pvh-line); }
.pvh-config__inner { max-width: 880px; margin: 0 auto; }
.pvh-config__head { margin-bottom: 32px; }
.pvh-config__step { color: var(--pvh-cta); font-size: 12px; letter-spacing: 0.08em; font-weight: 600; margin-bottom: 10px; }
.pvh-config__head h2 { font-size: clamp(32px, 4.5vw, 48px); }
.pvh-config__kicker { color: var(--pvh-sub); margin-top: 8px; font-size: 15px; }
.pvh-config__prog { display: flex; align-items: center; gap: 14px; margin-top: 20px; }
.pvh-config__prog span { color: var(--pvh-cta); font-size: 12px; min-width: 50px; }
.pvh-config__progBar { flex: 1; height: 2px; background: var(--pvh-line-soft); position: relative; overflow: hidden; }
.pvh-config__progBar span { position: absolute; left: 0; top: 0; bottom: 0; background: var(--pvh-cta); transition: width 320ms cubic-bezier(0.32,0.72,0,1); min-width: 0; }

.pvh-panel { background: var(--pvh-card); border: 1px solid var(--pvh-line-soft); border-radius: 6px; padding: 32px; }
.pvh-typGrid { display: grid; grid-template-columns: 1fr; gap: 10px; }
@media (min-width: 640px) { .pvh-typGrid { grid-template-columns: 1fr 1fr; } }
.pvh-tyg { background: var(--pvh-paper); border: 1px solid var(--pvh-line-soft); border-radius: 4px; padding: 18px; text-align: left; cursor: pointer; font-family: inherit; transition: all 180ms ease; display: flex; flex-direction: column; gap: 6px; }
.pvh-tyg:hover { border-color: var(--pvh-cta); }
.pvh-tyg--on { background: var(--pvh-cta); border-color: var(--pvh-cta); color: #fff; }
.pvh-tyg__h { font-weight: 700; font-size: 15px; color: inherit; }
.pvh-tyg__s { font-size: 13px; opacity: 0.75; }

.pvh-flach input[type=range] { width: 100%; accent-color: var(--pvh-cta); }
.pvh-flach__readout { text-align: center; padding: 18px 0; }
.pvh-flach__readout span { font-size: 48px; color: var(--pvh-cta); font-weight: 600; }
.pvh-flach__hint { color: var(--pvh-sub); font-size: 13px; text-align: center; }

.pvh-num { display: flex; align-items: stretch; border: 1px solid var(--pvh-line-soft); border-radius: 4px; background: var(--pvh-paper); overflow: hidden; }
.pvh-num input { flex: 1; background: transparent; border: none; padding: 16px 18px; font-size: 18px; color: var(--pvh-ink); font-family: 'JetBrains Mono', monospace; }
.pvh-num input:focus { outline: none; }
.pvh-num input::placeholder { color: var(--pvh-muted); }
.pvh-num__unit { padding: 0 18px; display: flex; align-items: center; color: var(--pvh-cta); border-left: 1px solid var(--pvh-line-soft); font-size: 14px; }

.pvh-panel__nav { display: flex; justify-content: space-between; margin-top: 28px; gap: 12px; }

/* Result */
.pvh-result__big { margin-bottom: 32px; padding-bottom: 32px; border-bottom: 1px solid var(--pvh-line-soft); }
.pvh-result__chip { color: var(--pvh-cta); font-size: 11px; letter-spacing: 0.1em; margin-bottom: 12px; }
.pvh-result__num { font-size: clamp(48px, 8vw, 80px); line-height: 1; font-weight: 600; color: var(--pvh-ink); margin-bottom: 12px; }
.pvh-result__num em { font-style: normal; color: var(--pvh-sub); font-size: 0.4em; margin-left: 10px; font-family: 'Space Grotesk', sans-serif; }
.pvh-result__sub { color: var(--pvh-sub); font-size: 15px; }
.pvh-result__sub strong { color: var(--pvh-ink); }
.pvh-result__matrix { list-style: none; padding: 0; margin: 0; display: grid; gap: 0; border: 1px solid var(--pvh-line-soft); border-radius: 4px; overflow: hidden; }
.pvh-result__matrix li { display: grid; grid-template-columns: 1fr auto auto; gap: 24px; padding: 16px 20px; border-bottom: 1px solid var(--pvh-line-soft); align-items: baseline; }
.pvh-result__matrix li:last-child { border-bottom: 0; }
.pvh-result__lbl { color: var(--pvh-sub); font-size: 14px; }
.pvh-result__v { font-size: 16px; color: var(--pvh-ink); font-weight: 600; }
.pvh-result__cost { font-size: 14px; color: var(--pvh-cta); }
@media (max-width: 540px) { .pvh-result__matrix li { grid-template-columns: 1fr; gap: 4px; } }

.pvh-method { margin-top: 24px; }
.pvh-method summary { cursor: pointer; color: var(--pvh-sub); font-size: 13px; }
.pvh-method ul { margin: 12px 0 0; padding-left: 18px; color: var(--pvh-sub); font-size: 13px; display: grid; gap: 6px; }

/* Lead */
.pvh-lead { margin-top: 32px; padding-top: 32px; border-top: 1px solid var(--pvh-line-soft); }
.pvh-lead__chip { color: var(--pvh-cta); font-size: 11px; letter-spacing: 0.1em; margin-bottom: 12px; }
.pvh-lead h3 { font-size: 24px; margin-bottom: 8px; }
.pvh-lead > p { color: var(--pvh-sub); margin-bottom: 20px; font-size: 14px; }
.pvh-form { display: grid; gap: 14px; }
.pvh-form__row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.pvh-in { display: flex; flex-direction: column; gap: 6px; }
.pvh-in > span { font-size: 13px; font-weight: 600; color: var(--pvh-ink); }
.pvh-in em { font-weight: 400; color: var(--pvh-muted); font-style: normal; }
.pvh-in input { padding: 12px 14px; font-size: 15px; border: 1px solid var(--pvh-line-soft); border-radius: 4px; background: var(--pvh-paper); color: var(--pvh-ink); font-family: inherit; }
.pvh-in input:focus { outline: none; border-color: var(--pvh-cta); }
.pvh-in--err input { border-color: #ef4444; }
.pvh-consent { display: flex; gap: 12px; padding: 14px; background: var(--pvh-paper); border: 1px solid var(--pvh-line-soft); border-radius: 4px; font-size: 12px; color: var(--pvh-sub); line-height: 1.5; cursor: pointer; }
.pvh-consent input { margin-top: 2px; flex-shrink: 0; accent-color: var(--pvh-cta); }
.pvh-err { color: #ef4444; font-size: 13px; text-align: center; }

.pvh-done { text-align: center; padding: 32px 0; }
.pvh-done__chip { color: var(--pvh-cta); font-size: 11px; letter-spacing: 0.1em; margin-bottom: 12px; }
.pvh-done h3 { font-size: 26px; margin-bottom: 8px; }
.pvh-done p { color: var(--pvh-sub); }

/* Methodology */
.pvh-meth { padding: 96px 24px; background: var(--pvh-bg); }
.pvh-meth__inner { max-width: 1120px; margin: 0 auto; }
.pvh-meth__chip { color: var(--pvh-cta); font-size: 11px; letter-spacing: 0.1em; margin-bottom: 12px; }
.pvh-meth h2 { font-size: clamp(32px, 4.5vw, 52px); margin-bottom: 48px; }
.pvh-meth__grid { display: grid; grid-template-columns: 1fr; gap: 32px; }
@media (min-width: 720px) { .pvh-meth__grid { grid-template-columns: 1fr 1fr; gap: 48px; } }
.pvh-meth__grid h4 { font-family: 'Space Grotesk', sans-serif; font-size: 18px; font-weight: 700; margin-bottom: 8px; }
.pvh-meth__grid p { color: var(--pvh-sub); font-size: 15px; max-width: 56ch; }
.pvh-meth__no { color: var(--pvh-cta); font-size: 13px; margin-bottom: 10px; letter-spacing: 0.06em; }

/* FAQ */
.pvh-faq { padding: 80px 24px; background: var(--pvh-paper); border-top: 1px solid var(--pvh-line); }
.pvh-faq__inner { max-width: 1100px; margin: 0 auto; }
.pvh-faq h2 { font-size: clamp(28px, 4vw, 40px); margin-bottom: 32px; }
.pvh-faqGrid { display: grid; grid-template-columns: 1fr; gap: 28px; }
@media (min-width: 720px) { .pvh-faqGrid { grid-template-columns: repeat(3, 1fr); gap: 40px; } }
.pvh-faqGrid h4 { font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 700; margin-bottom: 8px; color: var(--pvh-ink); }
.pvh-faqGrid p { color: var(--pvh-sub); font-size: 14px; }

.pvh-foot { background: #050a13; padding: 32px 24px; border-top: 1px solid var(--pvh-line); }
.pvh-foot__inner { max-width: 1320px; margin: 0 auto; display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 16px; font-size: 13px; color: var(--pvh-sub); }
.pvh-foot nav { display: flex; gap: 18px; }
.pvh-foot a { color: var(--pvh-sub); text-decoration: none; }
.pvh-foot a:hover { color: var(--pvh-cta); }
.pvh-foot__copy { font-size: 12px; opacity: 0.7; }

.pvh-stick { position: fixed; left: 0; right: 0; bottom: 0; padding-bottom: env(safe-area-inset-bottom); background: rgba(11, 18, 32, 0.95); backdrop-filter: blur(8px); border-top: 1px solid var(--pvh-line-soft); z-index: 40; }
.pvh-stick__inner { max-width: 1320px; margin: 0 auto; padding: 12px 20px; display: flex; justify-content: space-between; align-items: center; gap: 14px; color: var(--pvh-ink); font-size: 13px; }
@media (min-width: 980px) { .pvh-stick { display: none; } }

@media (prefers-reduced-motion: reduce) {
    .pvh *, .pvh *::before, .pvh *::after { transition: none !important; animation: none !important; transform: none !important; }
}

</style>
